import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { installationAPI } from '../api/installation';
import { backupLogAPI } from '../api/backupLog';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [runningBackups, setRunningBackups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // Poll for running backups every 30 seconds
    const interval = setInterval(fetchRunningBackups, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, runningResponse] = await Promise.all([
        installationAPI.getStatistics(),
        backupLogAPI.getRunningBackups()
      ]);
      setStats(statsResponse.data);
      setRunningBackups(runningResponse.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRunningBackups = async () => {
    try {
      const response = await backupLogAPI.getRunningBackups();
      setRunningBackups(response.data);
    } catch (error) {
      console.error('Failed to fetch running backups:', error);
    }
  };

  const getTimeSince = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Welcome, {user?.username}!</h1>
        <p>Manage your backup installations and configurations</p>
      </div>

      {/* Running Backups Alert */}
      {runningBackups.length > 0 && (
        <div className="running-backups-alert">
          <div className="alert-header">
            <h3>⏳ {runningBackups.length} Backup(s) In Progress</h3>
          </div>
          <div className="running-list">
            {runningBackups.map((backup) => (
              <div key={backup._id} className="running-item">
                <div className="running-info">
                  <strong>{backup.backupConfig?.configName}</strong>
                  <span className="installation-name">
                    {backup.installation?.installationName}
                  </span>
                </div>
                <div className="running-meta">
                  <span className="status-badge status-in_progress">
                    {backup.status}
                  </span>
                  <span className="time-info">
                    Started {getTimeSince(backup.startTime)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🖥️</div>
          <div className="stat-content">
            <h3>{stats?.totalInstallations || 0}</h3>
            <p>Total Installations</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{stats?.activeInstallations || 0}</h3>
            <p>Active Installations</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{stats?.totalBackupConfigs || 0}</h3>
            <p>Backup Configurations</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🔒</div>
          <div className="stat-content">
            <h3>{stats?.lockedInstallations || 0}</h3>
            <p>Locked Installations</p>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="recent-syncs">
          <h2>Recent Syncs</h2>
          {stats?.recentSyncs && stats.recentSyncs.length > 0 ? (
            <div className="syncs-list">
              {stats.recentSyncs.map((sync) => (
                <div key={sync.uniqueId} className="sync-item">
                  <div>
                    <strong>{sync.installationName}</strong>
                    {sync.computerName && <span> ({sync.computerName})</span>}
                  </div>
                  <div className="sync-time">
                    {new Date(sync.lastSyncedAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">No recent syncs</p>
          )}
        </div>

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <Link to="/installations/create" className="action-card">
              <span className="action-icon">➕</span>
              <span>Create Installation</span>
            </Link>
            <Link to="/installations" className="action-card">
              <span className="action-icon">📋</span>
              <span>View Installations</span>
            </Link>
            <Link to="/backup-logs" className="action-card">
              <span className="action-icon">📊</span>
              <span>View All Logs</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;